import type { PendingToolCall } from './types'

type Resolver = (result: unknown) => void
type Rejecter = (reason: Error) => void

const TOOL_PAUSE_TIMEOUT_MS = 5 * 60 * 1000

export class ToolPauseState {
  private readonly resolvers = new Map<string, Resolver>()
  private readonly rejecters = new Map<string, Rejecter>()
  private readonly pending = new Map<string, PendingToolCall>()
  private readonly timers = new Map<string, ReturnType<typeof setTimeout>>()

  register(
    toolCallId: string,
    pendingCall: PendingToolCall,
    resolve: Resolver,
    reject: Rejecter,
  ): void {
    if (this.resolvers.has(toolCallId)) {
      throw new Error(
        `tool-pause: tool call ${toolCallId} is already pending — resolve it before registering again`,
      )
    }
    this.resolvers.set(toolCallId, resolve)
    this.rejecters.set(toolCallId, reject)
    this.pending.set(toolCallId, pendingCall)

    const timer = setTimeout(() => {
      this.clearEntry(toolCallId)
      reject(new Error(`tool-pause: tool call ${toolCallId} timed out`))
    }, TOOL_PAUSE_TIMEOUT_MS)
    this.timers.set(toolCallId, timer)
  }

  private clearEntry(toolCallId: string): void {
    clearTimeout(this.timers.get(toolCallId))
    this.resolvers.delete(toolCallId)
    this.rejecters.delete(toolCallId)
    this.pending.delete(toolCallId)
    this.timers.delete(toolCallId)
  }

  resolve(toolCallId: string, result: unknown): void {
    const resolver = this.resolvers.get(toolCallId)
    if (!resolver) return
    this.clearEntry(toolCallId)
    resolver(result)
  }

  getPending(): PendingToolCall[] {
    return Array.from(this.pending.values())
  }
}
